import React, { useState } from "react";
import {
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    ExternalLink,
    Globe,
    TrendingDown,
    TrendingUp,
} from "lucide-react";

function tl(n, fmt) {
    if (fmt) return fmt;
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    if (Math.abs(v) < 0.005) return "—";
    const whole = Math.abs(v - Math.round(v)) < 0.01;
    if (whole) return `₺${Math.round(v).toLocaleString("tr-TR")}`;
    const [main, dec] = v.toFixed(2).split(".");
    return `₺${Number(main).toLocaleString("tr-TR")},${dec}`;
}

function DurumBadge({ durum, label }) {
    const cls =
        durum === "yuksek"
            ? "bg-rose-500/15 text-rose-200 border-rose-500/30"
            : durum === "dusuk"
              ? "bg-sky-500/15 text-sky-200 border-sky-500/30"
              : durum === "uyumlu"
                ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
                : "bg-slate-500/15 text-slate-400 border-slate-500/30";
    const Icon =
        durum === "yuksek" ? TrendingUp : durum === "dusuk" ? TrendingDown : CheckCircle2;
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${cls}`}
        >
            <Icon className="h-3 w-3" />
            {label || durum}
        </span>
    );
}

function ParcaSatirDetay({ satir }) {
    const [acik, setAcik] = useState(false);
    const ornekler = satir.piyasa_ornekler || [];

    return (
        <>
            <tr
                className={`border-t border-white/5 cursor-pointer hover:bg-white/[0.02] ${
                    satir.durum === "yuksek" ? "bg-rose-500/[0.04]" : ""
                }`}
                onClick={() => setAcik((v) => !v)}
            >
                <td className="p-2 text-white">
                    <span className="inline-flex items-center gap-1">
                        {acik ? (
                            <ChevronDown className="h-3 w-3 text-slate-500" />
                        ) : (
                            <ChevronRight className="h-3 w-3 text-slate-500" />
                        )}
                        {satir.parca_adi}
                    </span>
                </td>
                <td className="p-2 text-slate-300">
                    {tl(
                        satir.fatura_tutar ?? satir.ekspertiz_tutar,
                        satir.fatura_tutar_fmt ?? satir.ekspertiz_tutar_fmt
                    )}
                </td>
                <td className="p-2 text-violet-300">
                    {tl(satir.piyasa_orijinal_medyan, satir.piyasa_orijinal_medyan_fmt)}
                </td>
                <td className="p-2 text-amber-300/90">
                    {tl(satir.piyasa_muadil_medyan, satir.piyasa_muadil_medyan_fmt)}
                </td>
                <td className="p-2 text-emerald-300">
                    {tl(satir.piyasa_medyan, satir.piyasa_medyan_fmt)}
                </td>
                <td className="p-2">
                    <DurumBadge durum={satir.durum} label={satir.durum_label} />
                </td>
                <td className="p-2 text-xs text-slate-400 max-w-[200px] truncate">
                    {satir.fark_tl_fmt || satir.mesaj || "—"}
                </td>
            </tr>
            {acik ? (
                <tr className="bg-black/30">
                    <td colSpan={7} className="p-3">
                        <p className="text-xs text-slate-400 mb-2">{satir.mesaj}</p>
                        {ornekler.length ? (
                            <div className="space-y-2">
                                <div className="text-[10px] uppercase text-slate-500">
                                    Örnek ilanlar / web sonuçları
                                </div>
                                {ornekler.map((o, i) => (
                                    <div
                                        key={i}
                                        className="flex flex-wrap items-baseline gap-2 text-xs border-b border-white/5 pb-2"
                                    >
                                        <span className="text-slate-500 shrink-0">
                                            [{o.tip}] {o.kaynak}
                                        </span>
                                        <span className="text-emerald-300 font-medium">
                                            {o.fiyat_fmt}
                                        </span>
                                        {o.url ? (
                                            <a
                                                href={o.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-violet-300 hover:underline inline-flex items-center gap-1"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {o.baslik?.slice(0, 80)}
                                                <ExternalLink className="h-3 w-3" />
                                            </a>
                                        ) : (
                                            <span className="text-slate-400">{o.baslik}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-amber-400/80 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Bu parça için örnek ilan bulunamadı; AI tahmini kullanılmış olabilir.
                            </p>
                        )}
                        {satir.piyasa_guven != null ? (
                            <p className="text-[10px] text-slate-600 mt-2">
                                Güven skoru: %{satir.piyasa_guven}
                            </p>
                        ) : null}
                    </td>
                </tr>
            ) : null}
        </>
    );
}

export default function ParcaPiyasaPanel({ satirlar, piyasaOzet, parcaPiyasa, aracKontekst, loading }) {
    const taranan = satirlar?.filter((s) => s.piyasa_medyan != null || s.durum);
    const hata = parcaPiyasa?.hata || parcaPiyasa?.error;

    if (!satirlar?.length && !loading) return null;

    return (
        <div className="rounded-xl border border-violet-500/25 bg-violet-500/[0.04] overflow-hidden">
            <div className="px-4 py-3 border-b border-violet-500/20 flex flex-wrap items-center gap-2">
                <Globe className="h-4 w-4 text-violet-300" />
                <h3 className="text-sm font-medium text-white">
                    Parça piyasa istihbaratı — internet araştırması
                </h3>
                {aracKontekst?.tam_etiket ? (
                    <span className="text-xs text-slate-500 ml-auto">{aracKontekst.tam_etiket}</span>
                ) : null}
            </div>

            {loading ? (
                <p className="p-4 text-sm text-slate-400 animate-pulse">
                    Parçalar taranıyor… Hızlı mod ~1 dk, tam analiz ~2–4 dk sürebilir.
                </p>
            ) : null}

            {hata && !aracKontekst?.arastirma_hazir ? (
                <p className="p-4 text-sm text-amber-300">{hata}</p>
            ) : null}

            {piyasaOzet?.mesaj ? (
                <div className="px-4 py-3 border-b border-white/5 grid sm:grid-cols-4 gap-3 text-center">
                    <div>
                        <div className="text-[10px] text-slate-500 uppercase">Taranan</div>
                        <div className="text-lg text-white">{piyasaOzet.taranan_parca ?? "—"}</div>
                    </div>
                    <div>
                        <div className="text-[10px] text-rose-400/80 uppercase">Yüksek</div>
                        <div className="text-lg text-rose-300">{piyasaOzet.yuksek_sayisi ?? 0}</div>
                    </div>
                    <div>
                        <div className="text-[10px] text-emerald-400/80 uppercase">Uyumlu</div>
                        <div className="text-lg text-emerald-300">{piyasaOzet.uyumlu_sayisi ?? 0}</div>
                    </div>
                    <div>
                        <div className="text-[10px] text-slate-500 uppercase">Genel fark</div>
                        <div className="text-lg text-[#D4AF37]">
                            {piyasaOzet.genel_fark_tl_fmt || "—"}
                        </div>
                    </div>
                </div>
            ) : null}

            {piyasaOzet?.mesaj ? (
                <p className="px-4 py-2 text-xs text-slate-400 border-b border-white/5">
                    {piyasaOzet.mesaj}
                    {piyasaOzet.veri_yok_sayisi ? (
                        <span className="text-amber-400/90 block mt-1">
                            {piyasaOzet.veri_yok_sayisi} kalemde piyasa verisi yok (çoğu işçilik/boya
                            satırıdır — yedek parça satırlarında ilan aranır).
                        </span>
                    ) : null}
                </p>
            ) : null}

            <div className="overflow-x-auto max-h-[520px]">
                <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#0f172a] z-10">
                        <tr className="text-left text-slate-500">
                            <th className="p-2">Parça</th>
                            <th className="p-2">Rapor tutarı</th>
                            <th className="p-2">Orijinal medyan</th>
                            <th className="p-2">Muadil medyan</th>
                            <th className="p-2">Piyasa ref.</th>
                            <th className="p-2">Sonuç</th>
                            <th className="p-2">Fark</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(taranan?.length ? taranan : satirlar).map((s, i) => (
                            <ParcaSatirDetay key={`${s.parca_adi}-${i}`} satir={s} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
